/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { CircleStackIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { Bucket } from '../../types';
import { formatBytes } from '../../utils/formateSize';
import BucketActionMenu from './BucketActionMenu';
import toast from 'react-hot-toast';

// Format currency in INR
const formatCurrency = (amount: number): string => {
    if (amount === 0) return '₹0.00';
    if (amount < 1) return `₹${amount.toFixed(4)}`;
    return `₹${amount.toFixed(2)}`;
};

interface BucketListTableProps {
    buckets: Bucket[];
    onSelectBucket: (bucket: Bucket) => void;
    onDeleteBucket: (bucket: Bucket) => void;
}

const BucketListTable: React.FC<BucketListTableProps> = ({
    buckets,
    onSelectBucket,
    onDeleteBucket,
}) => {
    const [activeActionMenu, setActiveActionMenu] = useState<string | null>(null);
    const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

    const handleOpenActionMenu = (bucketId: string, event: React.MouseEvent<HTMLButtonElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom + 4,
            right: window.innerWidth - rect.right,
        });
        setActiveActionMenu(bucketId);
    };

    const closeActionMenu = () => {
        setActiveActionMenu(null);
        setMenuPosition(null);
    };

    const handleView = (bucket: Bucket) => {
        onSelectBucket(bucket);
        closeActionMenu();
    };

    const handleEdit = () => {
        toast.success('Edit functionality coming soon');
        closeActionMenu();
    };

    const handleDelete = (bucket: Bucket) => {
        onDeleteBucket(bucket);
        closeActionMenu();
    };

    return (
        <>
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-800 border-b border-gray-700">
                        <tr>
                            <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Name
                            </th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Visibility
                            </th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Versioning
                            </th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Cost
                            </th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Created
                            </th>
                            <th className="text-right px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                        {buckets.map((bucket: Bucket) => (
                            <tr key={bucket.id} data-bucket-id={bucket.id} className="hover:bg-gray-800">
                                <td className="px-6 py-4">
                                    <button
                                        onClick={() => onSelectBucket(bucket)}
                                        className="flex items-center gap-3 text-left group"
                                    >
                                        <div className="p-2 bg-primary-500/20 rounded-lg group-hover:bg-primary-500/30 transition-colors">
                                            <CircleStackIcon className="w-5 h-5 text-primary-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-white group-hover:text-primary-500 transition-colors">
                                                {bucket.name}
                                            </p>
                                            <p className="text-sm text-gray-400">
                                                {bucket.object_count || 0} objects • {formatBytes(bucket.current_size_bytes || 0)}
                                            </p>
                                        </div>
                                    </button>
                                </td>
                                <td className="px-6 py-4">
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bucket.is_public
                                            ? 'bg-yellow-500/20 text-yellow-400'
                                            : 'bg-gray-700 text-gray-300'
                                            }`}
                                    >
                                        {bucket.is_public ? 'Public' : 'Private'}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <span className={`text-sm ${bucket.versioning ? 'text-green-400' : 'text-gray-400'}`}>
                                        {bucket.versioning ? 'On' : 'Off'}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <span className="text-sm font-medium text-white">
                                        {formatCurrency(bucket.total_billed_inr || 0)}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-400">
                                    {formatDistanceToNow(new Date(bucket.created_at), { addSuffix: true })}
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center justify-end">
                                        <button
                                            onClick={(e) => handleOpenActionMenu(bucket.id, e)}
                                            className="p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded-lg"
                                        >
                                            <EllipsisVerticalIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Action Menu */}
            {activeActionMenu && menuPosition && (() => {
                const bucket = buckets.find((b) => b.id === activeActionMenu);
                if (!bucket) return null;
                return (
                    <BucketActionMenu
                        bucket={bucket}
                        position={menuPosition}
                        onView={() => handleView(bucket)}
                        onEdit={handleEdit}
                        onDelete={() => handleDelete(bucket)}
                        onClose={closeActionMenu}
                    />
                );
            })()}
        </>
    );
};

export default BucketListTable;
